import {
  Modal,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  InputLabel,
  Switch,
} from "@mui/material";
import { useState, useEffect } from "react";
import {
  CloseButton,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalTitleContainer,
  ModalFooter,
  ModalContent,
} from "./styles";
import { X, AlertTriangle } from "lucide-react";
import { IAvailableExternalFontViewModel } from "viewModels";
import { FontAccessLevel, SearchType } from "types/enums";
import { useExternalFont } from "contexts/externalFontContext";

interface ModalProps {
  font: IAvailableExternalFontViewModel;
  open: boolean;
  onClose: () => void;
}

const AVAILABLE_PARAMETERS = [
  { value: "document", label: "Documento" },
  { value: "process_number", label: "Número do Processo" },
  { value: "cep", label: "CEP" },
  { value: "address", label: "Endereço" },
  { value: "neighborhood", label: "Bairro" },
  { value: "city", label: "Cidade" },
  { value: "uf", label: "UF" },
  { value: "name", label: "Nome" },
  { value: "identity_number", label: "Número da Identidade" },
  { value: "expedition_organ", label: "Órgão Expedidor" },
  { value: "mother_name", label: "Nome da Mãe" },
  { value: "father_name", label: "Nome do Pai" },
  { value: "gender", label: "Gênero" },
  { value: "key_words", label: "Palavras-chave" },
];

const SEARCH_TYPE_OPTIONS = [
  { value: SearchType.CPF, label: "CPF" },
  { value: SearchType.CNPJ, label: "CNPJ" },
  { value: SearchType.BOTH, label: "CPF/CNPJ" },
];

export default function EditExternalFontModal({
  font,
  open,
  onClose,
}: ModalProps) {
  const { editExternalFont } = useExternalFont();
  const [formData, setFormData] =
    useState<IAvailableExternalFontViewModel>(font);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [keyEditEnabled, setKeyEditEnabled] = useState(false);

  const handleInputChange = (
    field: keyof IAvailableExternalFontViewModel,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleParameterToggle = (parameter: string) => {
    const currentParams = formData.requiredParameters;
    const newParams = currentParams.includes(parameter)
      ? currentParams.filter((p) => p !== parameter)
      : [...currentParams, parameter];

    handleInputChange("requiredParameters", newParams);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    }

    if (!formData.key.trim()) {
      newErrors.key = "Key é obrigatória";
    }

    if (formData.requiredParameters.length === 0) {
      newErrors.requiredParameters = "Selecione pelo menos um parâmetro";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (validateForm()) {
      await editExternalFont({
        key: formData.key,
        name: formData.name,
        accessLevel: formData.accessLevel,
        type: formData.type,
        disabled: formData.disabled,
        disabledReason: formData.disabledReason,
        requiredParameters: formData.requiredParameters,
      });
      onClose();
    }
  };

  const handleCancel = () => {
    setFormData(font);
    setErrors({});
    setKeyEditEnabled(false);
    onClose();
  };

  useEffect(() => {
    setFormData(font);
    setErrors({});
    setKeyEditEnabled(false);
  }, [font]);

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      sx={{ display: "flex", flexDirection: "column" }}
    >
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          borderRadius: 2,
          outline: "none",
          width: { xs: "90vw", sm: "80vw", md: "60vw" },
          minHeight: { xs: "70vh", sm: "80vh" },
          maxWidth: "100%",
        }}
      >
        <ModalContainer>
          <ModalHeader>
            <ModalTitleContainer>
              <ModalTitle id="modal-title">Edição da fonte</ModalTitle>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Edite os detalhes da fonte {font.name}
              </Typography>
            </ModalTitleContainer>
            <CloseButton onClick={handleCancel}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalContent>
            <Stack spacing={3}>
              <TextField
                label="Nome da Fonte"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
                fullWidth
                size="small"
                variant="outlined"
              />

              <Box>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <TextField
                    label="Key"
                    value={formData.key}
                    onChange={(e) => handleInputChange("key", e.target.value)}
                    error={!!errors.key}
                    helperText={errors.key}
                    required
                    fullWidth
                    size="small"
                    variant="outlined"
                    disabled={!keyEditEnabled}
                    sx={{
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
                      },
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={keyEditEnabled}
                        onChange={(e) => setKeyEditEnabled(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.75rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Editar
                      </Typography>
                    }
                    sx={{ mt: 0.5 }}
                  />
                </Stack>

                <Alert
                  severity="warning"
                  icon={<AlertTriangle size={16} />}
                  sx={{ mt: 1, fontSize: "0.875rem" }}
                >
                  <Typography variant="body2">
                    <strong>Atenção:</strong> Alterar a key pode tornar a fonte
                    não funcional na plataforma. Certifique-se de que a nova key
                    está correta antes de salvar.
                  </Typography>
                </Alert>
              </Box>

              <FormControl fullWidth required size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={formData.type}
                  label="Tipo"
                  onChange={(e) =>
                    handleInputChange("type", Number(e.target.value))
                  }
                >
                  {SEARCH_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.disabled ? "inactive" : "active"}
                  label="Status"
                  onChange={(e) => {
                    const isDisabled = e.target.value === "inactive";
                    handleInputChange("disabled", isDisabled);
                    if (!isDisabled) {
                      handleInputChange("disabledReason", "");
                    }
                  }}
                >
                  <MenuItem value="active">Ativa</MenuItem>
                  <MenuItem value="inactive">Inativa</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <>
                    <InputLabel>Acesso</InputLabel>
                    <Switch
                      checked={formData.accessLevel === FontAccessLevel.PUBLIC}
                      onChange={(e) =>
                        handleInputChange(
                          "accessLevel",
                          e.target.checked
                            ? FontAccessLevel.PUBLIC
                            : FontAccessLevel.PRIVATE
                        )
                      }
                      size="small"
                    />
                  </>
                }
                label={
                  <Typography variant="body2">
                    {formData.accessLevel === FontAccessLevel.PUBLIC
                      ? "Pública"
                      : "Privada"}
                  </Typography>
                }
              />

              {formData.disabled && (
                <TextField
                  label="Motivo da Inativação"
                  value={formData.disabledReason || ""}
                  onChange={(e) =>
                    handleInputChange("disabledReason", e.target.value)
                  }
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  variant="outlined"
                />
              )}

              <Box>
                <FormLabel
                  component="legend"
                  required
                  sx={{ mb: 1, color: "text.primary" }}
                >
                  Parâmetros Requeridos
                </FormLabel>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1,
                    maxHeight: 200,
                    overflow: "auto",
                    bgcolor: "grey.50",
                  }}
                >
                  <FormGroup>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, 1fr)",
                          md: "repeat(3, 1fr)",
                        },
                        gap: 0.25,
                      }}
                    >
                      {AVAILABLE_PARAMETERS.map((param) => (
                        <FormControlLabel
                          key={param.value}
                          control={
                            <Checkbox
                              checked={formData.requiredParameters.includes(
                                param.value
                              )}
                              onChange={() =>
                                handleParameterToggle(param.value)
                              }
                              size="small"
                              sx={{ p: 0.25 }}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.75rem", ml: 0.25 }}
                            >
                              {param.label}
                            </Typography>
                          }
                          sx={{
                            m: 0,
                            p: 0.25,
                            borderRadius: 1,
                            bgcolor: formData.requiredParameters.includes(
                              param.value
                            )
                              ? "grey.100"
                              : "transparent",
                            "&:hover": {
                              bgcolor: "grey.200",
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </FormGroup>
                </Paper>
                {errors.requiredParameters && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errors.requiredParameters}
                  </Alert>
                )}
              </Box>
            </Stack>
          </ModalContent>

          <ModalFooter>
            <Stack
              direction={{ xs: "column-reverse", sm: "row" }}
              spacing={2}
              sx={{ ml: "auto" }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{ minWidth: 120 }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{ minWidth: 120 }}
              >
                Salvar Alterações
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContainer>
      </Paper>
    </Modal>
  );
}
